import { useState } from 'react';
import { Popup } from 'react-map-gl/mapbox';
import { Loader2, X, Phone, Mail, MapPin, Vote, Hash, ChevronLeft, ChevronRight } from 'lucide-react';
import { useVoter } from '@/hooks/queries/useVoters';
import { cn } from '@/lib/utils';
import type { VoterSupport, ContactStatus } from '@signalvote/shared';

interface VoterDetailPopupProps {
  voterIds: string[];
  lngLat: [number, number];
  onClose: () => void;
}

const SUPPORT_LABELS: Record<string, { label: string; className: string }> = {
  StrongYes: { label: 'Strong Yes', className: 'bg-green-100 text-green-800' },
  LeaningYes: { label: 'Leaning Yes', className: 'bg-green-50 text-green-700' },
  Undecided: { label: 'Undecided', className: 'bg-yellow-50 text-yellow-800' },
  LeaningNo: { label: 'Leaning No', className: 'bg-red-50 text-red-700' },
  StrongNo: { label: 'Strong No', className: 'bg-red-100 text-red-800' },
};

const CONTACT_LABELS: Record<string, string> = {
  Reached: 'Reached',
  NotHome: 'Not Home',
  Refused: 'Refused',
  Moved: 'Moved',
  Deceased: 'Deceased',
};

function VoterCard({ voterId }: { voterId: string }) {
  const { data: voter, isLoading } = useVoter(voterId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!voter) {
    return (
      <p className="py-3 text-center text-xs text-muted-foreground">
        Voter not found
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-foreground">
          {voter.firstName} {voter.lastName}
        </h3>
        <p className="text-xs text-muted-foreground">
          {voter.age && `${voter.age} yrs`}
          {voter.age && voter.gender && ' · '}
          {voter.gender}
          {voter.partyAffiliation && ` · ${voter.partyAffiliation}`}
        </p>
        {voter.lalVoterId && (
          <p className="text-[11px] text-muted-foreground/70">
            <Hash className="mr-0.5 inline h-2.5 w-2.5" />
            {voter.lalVoterId}
          </p>
        )}
      </div>

      {/* Address */}
      <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          {[voter.addressLine, voter.city, [voter.state, voter.zip].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ')}
        </span>
      </div>

      {/* Contact info */}
      {(voter.cellPhone || voter.email) && (
        <div className="space-y-1">
          {voter.cellPhone && (
            <div className="flex items-center gap-1.5 text-xs">
              <Phone className="h-3 w-3 text-muted-foreground" />
              <a href={`tel:${voter.cellPhone}`} className="text-blue-600 hover:underline">
                {voter.cellPhone}
              </a>
            </div>
          )}
          {voter.email && (
            <div className="flex items-center gap-1.5 text-xs">
              <Mail className="h-3 w-3 text-muted-foreground" />
              <a href={`mailto:${voter.email}`} className="text-blue-600 hover:underline truncate">
                {voter.email}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Status badges */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
            voter.isContacted
              ? 'bg-orange-100 text-orange-800'
              : 'bg-blue-100 text-blue-800',
          )}
        >
          {voter.isContacted
            ? CONTACT_LABELS[voter.lastContactStatus as string] ?? 'Contacted'
            : 'Not Contacted'}
        </span>

        {voter.voterSupport && SUPPORT_LABELS[voter.voterSupport as string] && (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
              SUPPORT_LABELS[voter.voterSupport as string].className,
            )}
          >
            {SUPPORT_LABELS[voter.voterSupport as string].label}
          </span>
        )}

        {voter.voteFrequency && (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            <Vote className="h-2.5 w-2.5" />
            {voter.voteFrequency}
          </span>
        )}
      </div>

      {/* Tags */}
      {voter.tags && voter.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {voter.tags.map((tag) => (
            <span
              key={tag.id}
              className="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: tag.color ? `${tag.color}20` : '#e5e7eb',
                color: tag.color ?? '#6b7280',
              }}
            >
              {tag.tagName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function VoterDetailPopup({ voterIds, lngLat, onClose }: VoterDetailPopupProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = voterIds.length;
  const hasMultiple = total > 1;

  return (
    <Popup
      longitude={lngLat[0]}
      latitude={lngLat[1]}
      anchor="bottom"
      onClose={onClose}
      closeOnClick={false}
      closeButton={false}
      maxWidth="320px"
      offset={12}
    >
      <div className="min-w-[260px] max-w-[300px]">
        {/* Close + pagination header */}
        <div className="flex items-center justify-between mb-1">
          {hasMultiple ? (
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentIndex((i) => (i - 1 + total) % total)}
                className="rounded p-0.5 hover:bg-muted"
              >
                <ChevronLeft className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              <span className="text-[11px] font-medium text-muted-foreground">
                {currentIndex + 1} of {total} voters
              </span>
              <button
                onClick={() => setCurrentIndex((i) => (i + 1) % total)}
                className="rounded p-0.5 hover:bg-muted"
              >
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <div />
          )}
          <button
            onClick={onClose}
            className="shrink-0 rounded p-0.5 hover:bg-muted"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>

        <VoterCard voterId={voterIds[currentIndex]} />
      </div>
    </Popup>
  );
}
